import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSectionContext } from '@/contexts/SectionContext';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  Users,
  Activity,
  Heart,
  TrendingUp,
  Calendar,
  FileText,
  Download,
  Loader2,
  AlertCircle,
  Stethoscope,
  PieChart as PieChartIcon,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { statisticsService, PatientsPerMonthResponse, ProcedureStatisticsResponse, DashboardStatisticsResponse } from '@/services/statisticsService';
import { CHART_COLORS } from '@/utils/constants';

const COLORS = [
  CHART_COLORS.primary,
  CHART_COLORS.secondary,
  CHART_COLORS.accent,
  CHART_COLORS.danger,
  CHART_COLORS.info,
  CHART_COLORS.success
];

interface HospitalStatisticsProps {
  className?: string;
}

export const HospitalStatistics: React.FC<HospitalStatisticsProps> = ({ className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // Data state
  const [patientsData, setPatientsData] = useState<PatientsPerMonthResponse | null>(null);
  const [proceduresData, setProceduresData] = useState<ProcedureStatisticsResponse | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardStatisticsResponse | null>(null);

  const { toast } = useToast();
  const { utilizator } = useAuth();
  const { activeSection } = useSectionContext();

  // Generate year options for the last 5 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 1 }, (_, i) => currentYear - i);

  useEffect(() => {
    // Check if user is authenticated before fetching
    if (utilizator) {
      fetchDashboardStatistics();
    }
  }, [utilizator, activeSection]);

  useEffect(() => {
    // Check if user is authenticated before fetching
    if (utilizator) {
      fetchStatisticsData();
    }
  }, [selectedYear, utilizator]);

  const fetchDashboardStatistics = async () => {
    try {
      setDashboardLoading(true);
      setError(null);
      const data = await statisticsService.getDashboardStatistics(activeSection ?? undefined);
      setDashboardData(data);
    } catch (err: any) {
      const errorMessage = err.message || 'Eroare la încărcarea statisticilor generale';
      setError(errorMessage);
      toast({
        variant: 'destructive',
        title: 'Eroare',
        description: errorMessage
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchStatisticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [patientsResponse, proceduresResponse] = await Promise.all([
        statisticsService.getPatientsPerMonth(selectedYear),
        statisticsService.getProcedureStatistics({ year: selectedYear })
      ]);

      setPatientsData(patientsResponse);
      setProceduresData(proceduresResponse);

      toast({
        title: 'Succes',
        description: `Statistici actualizate pentru anul ${selectedYear}`
      });

    } catch (err: any) {
      const errorMessage = err.message || 'Eroare la încărcarea statisticilor';
      setError(errorMessage);
      toast({
        variant: 'destructive',
        title: 'Eroare',
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStatisticsData();
    fetchDashboardStatistics();
  };

  const handleExportPDF = async () => {
    try {
      toast({
        title: 'Export',
        description: 'Funcționalitatea de export va fi implementată în curând'
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Eroare',
        description: 'Eroare la exportul raportului'
      });
    }
  };

  // Format chart data
  const formatPatientsChartData = (data: PatientsPerMonthResponse) => {
    if (!data || !data.monthly_data) return [];

    return data.monthly_data.map(item => ({
      luna: item.month_name,
      pacienti: item.patient_count || 0,
      luna_num: item.month
    }));
  };

  const formatProceduresChartData = (data: ProcedureStatisticsResponse) => {
    if (!data || !data.monthly_trend) return [];

    return data.monthly_trend.map(item => ({
      luna: item.month_name,
      total: item.procedure_count || 0,
      chirurgii: item.surgery_count || 0,
      cateterizari: item.catheterization_count || 0
    }));
  };

  const formatProcedureTypesData = (data: ProcedureStatisticsResponse) => {
    if (!data || !data.procedure_types) return [];

    return data.procedure_types.map((type, index) => ({
      name: type.procedure_type_name,
      value: type.count || 0,
      color: COLORS[index % COLORS.length]
    }));
  };

  if (dashboardLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p className="text-muted-foreground">Se încarcă statisticile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Statistici - {utilizator?.hospital_name}
          </h1>
          <p className="text-muted-foreground">
            Monitorizarea activității și performanței spitalului
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={selectedYear.toString()}
            onValueChange={(value) => setSelectedYear(parseInt(value))}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Anul" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizează
          </Button>
          <Button
            size="sm"
            onClick={handleExportPDF}
          >
            <Download className="mr-2 h-4 w-4" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Dashboard Overview Cards */}
      {dashboardData && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total pacienți
              </CardTitle>
              <div className="rounded-full p-2 bg-blue-100">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.patients.total}</div>
              <p className="text-xs text-muted-foreground">
                +{dashboardData.patients.this_year} anul acesta
              </p>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span>♀ {dashboardData.patients.female}</span>
                <span>♂ {dashboardData.patients.male}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Proceduri totale
              </CardTitle>
              <div className="rounded-full p-2 bg-green-100">
                <Activity className="h-4 w-4 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.procedures.total}</div>
              <p className="text-xs text-muted-foreground">
                {dashboardData.procedures.this_year} anul acesta
              </p>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span>Chirurgii: {dashboardData.procedures.surgeries}</span>
                <span>Cateterizări: {dashboardData.procedures.catheterizations}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Diagnostice MCC
              </CardTitle>
              <div className="rounded-full p-2 bg-red-100">
                <Heart className="h-4 w-4 text-red-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.diagnoses.total_diagnoses}</div>
              <p className="text-xs text-muted-foreground">
                {dashboardData.diagnoses.patients_with_diagnoses} pacienți cu diagnostic
              </p>
              <div className="flex items-center justify-between mt-2 text-xs">
                <Badge variant="secondary" className="bg-red-100 text-red-800">
                  {dashboardData.patients.severe_chd_cases} cazuri severe
                </Badge>
                <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                  {dashboardData.patients.active_chd_cases} active
                </Badge>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  {dashboardData.patients.premature_cases} prematuri
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Coduri ICD-10
              </CardTitle>
              <div className="rounded-full p-2 bg-purple-100">
                <FileText className="h-4 w-4 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardData.diagnoses.unique_icd10_codes}</div>
              <p className="text-xs text-muted-foreground">
                coduri unice folosite
              </p>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span>Primari: {dashboardData.diagnoses.primary_diagnoses}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )
      }

      {/* Main Statistics Tabs */}
      <Tabs defaultValue="patients" className="space-y-4">
        <TabsList>
          <TabsTrigger value="patients">
            <Users className="mr-2 h-4 w-4" />
            Pacienți
          </TabsTrigger>
          <TabsTrigger value="procedures">
            <Stethoscope className="mr-2 h-4 w-4" />
            Proceduri
          </TabsTrigger>
          <TabsTrigger value="trends">
            <TrendingUp className="mr-2 h-4 w-4" />
            Tendințe
          </TabsTrigger>
        </TabsList>

        {/* Patients Tab */}
        <TabsContent value="patients" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Pacienți pe luni - {selectedYear}
              </CardTitle>
              <CardDescription>
                Numărul de pacienți înregistrați în fiecare lună
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : patientsData && patientsData.monthly_data && patientsData.monthly_data.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{patientsData.total_patients || 0}</p>
                      <p className="text-xs text-muted-foreground">Total pacienți</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{patientsData.summary?.months_with_data || 0}</p>
                      <p className="text-xs text-muted-foreground">Luni cu date</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{patientsData.summary?.average_per_month?.toFixed(1) || '0.0'}</p>
                      <p className="text-xs text-muted-foreground">Media pe lună</p>
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={formatPatientsChartData(patientsData)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="luna" />
                      <YAxis />
                      <Tooltip
                        labelStyle={{ color: '#374151' }}
                        contentStyle={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}
                      />
                      <Bar
                        dataKey="pacienti"
                        fill={CHART_COLORS.primary}
                        name="Pacienți"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Nu sunt date disponibile pentru anul {selectedYear}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Procedures Tab */}
        <TabsContent value="procedures" className="space-y-4">
          {proceduresData && proceduresData.metrics && (
            <>
              {/* Procedure Metrics Cards */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Total proceduri</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{proceduresData.metrics.total_procedures || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      {proceduresData.metrics.unique_patients || 0} pacienți unici
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Rata complicații</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{proceduresData.metrics.rates?.complication_rate?.toFixed(1) || '0.0'}%</div>
                    <p className="text-xs text-muted-foreground">
                      {proceduresData.metrics.procedures_with_complications || 0} cu complicații
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Mortalitate 30 zile</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{proceduresData.metrics.rates?.mortality_rate_30d?.toFixed(1) || '0.0'}%</div>
                    <p className="text-xs text-muted-foreground">
                      {proceduresData.metrics.mortality_30d || 0} cazuri
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Reintervenții 1 an</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{proceduresData.metrics.rates?.reintervention_rate_1y?.toFixed(1) || '0.0'}%</div>
                    <p className="text-xs text-muted-foreground">
                      {proceduresData.metrics.reinterventions_1y || 0} reintervenții
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Grid */}
              <div className="grid gap-4 lg:grid-cols-2">
                {/* Procedure Types Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChartIcon className="h-5 w-5" />
                      Distribuția tipurilor de proceduri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {formatProcedureTypesData(proceduresData).length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={formatProcedureTypesData(proceduresData)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${value}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {formatProcedureTypesData(proceduresData).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        Nu sunt date disponibile
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Procedures */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Top proceduri
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {proceduresData.top_procedures && proceduresData.top_procedures.length > 0 ? (
                        proceduresData.top_procedures.slice(0, 5).map((proc, index) => (
                          <div key={proc.procedure_code || index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{proc.procedure_name}</p>
                              <p className="text-xs text-muted-foreground">{proc.procedure_code}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold">{proc.count || 0}</p>
                              <p className="text-xs text-muted-foreground">
                                {proc.complication_rate?.toFixed(1) || '0.0'}% complicații
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          Nu sunt proceduri disponibile
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          {proceduresData && proceduresData.monthly_trend && proceduresData.monthly_trend.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Evoluția procedurilor pe luni - {selectedYear}
                </CardTitle>
                <CardDescription>
                  Tendința procedurilor chirurgicale și cateterizărilor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={formatProceduresChartData(proceduresData)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="luna" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stackId="1"
                      stroke={CHART_COLORS.primary}
                      fill={CHART_COLORS.primary}
                      name="Total proceduri"
                    />
                    <Area
                      type="monotone"
                      dataKey="chirurgii"
                      stackId="2"
                      stroke={CHART_COLORS.secondary}
                      fill={CHART_COLORS.secondary}
                      name="Chirurgii"
                    />
                    <Area
                      type="monotone"
                      dataKey="cateterizari"
                      stackId="2"
                      stroke={CHART_COLORS.accent}
                      fill={CHART_COLORS.accent}
                      name="Cateterizări"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Evoluția procedurilor pe luni - {selectedYear}
                </CardTitle>
                <CardDescription>
                  Tendința procedurilor chirurgicale și cateterizărilor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center h-[350px] text-muted-foreground">
                  Nu sunt date disponibile pentru anul {selectedYear}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div >
  );
};